import { useCallback, useState } from 'react';

import type { ValidationSearchParams } from '../api/types.js';
import type { DiscardMutationVariables } from '../queries/index.js';

export const useDiscard = ({
  dataFileId,
  searchParams,
  discardDataFileRows,
}: {
  dataFileId: string;
  searchParams: ValidationSearchParams;
  discardDataFileRows: (vars: DiscardMutationVariables) => Promise<unknown>;
}) => {
  const initialSelectionValue: {
    isInverted: boolean;
    selectedRows: Record<string, boolean>;
    rowIndicesById: Record<string, number>;
  } = { isInverted: false, selectedRows: {}, rowIndicesById: {} };
  const [selection, setSelection] = useState(initialSelectionValue);

  const handleSelectRow = useCallback(
    (rowId: string, rowIndex: number) => (checked: boolean) => {
      setSelection((prev) => ({
        ...prev,
        selectedRows: {
          ...prev.selectedRows,
          [rowId]: checked,
        },
        rowIndicesById: {
          ...prev.rowIndicesById,
          [rowId]: rowIndex,
        },
      }));
    },
    [setSelection],
  );

  const handleSelectAllRows = () => {
    setSelection((prev) => ({
      isInverted: !prev.isInverted,
      selectedRows: {},
      rowIndicesById: {},
    }));
  };

  const resetSelection = () => {
    setSelection(initialSelectionValue);
  };

  const selectedRowsIds = Object.keys(selection.selectedRows).filter((id) =>
    selection.isInverted
      ? selection.selectedRows[id] === false
      : selection.selectedRows[id],
  );

  const hasSelection = selection.isInverted || Boolean(selectedRowsIds.length);

  const discardRows = async () => {
    const selectionBeforeDiscard = selection;
    setSelection(initialSelectionValue);
    try {
      const payload = selectionBeforeDiscard.isInverted
        ? {
            excludeIds: selectedRowsIds,
            onlyErrors: Boolean(searchParams.onlyErrors),
          }
        : { includeIds: selectedRowsIds };

      const selectedRowsIndices = selectedRowsIds.map(
        (id) => selectionBeforeDiscard.rowIndicesById[id],
      );

      const meta = selectionBeforeDiscard.isInverted
        ? { selectedRowsIndices, excludeRowIndices: selectedRowsIndices }
        : { selectedRowsIndices, includeRowIndices: selectedRowsIndices };

      await discardDataFileRows({
        id: dataFileId,
        payload,
        meta,
      });
    } catch {
      setSelection(selectionBeforeDiscard);
    }
  };

  return {
    selection,
    selectedRowsIds,
    hasSelection,
    resetSelection,
    handleSelectAllRows,
    handleSelectRow,
    discardRows,
  };
};
